/**
 * Petro-Pass Canada Bulk Margin Manager (Phase 3E-9i, Feb 2026).
 *
 * Symmetric with `PetroPassUsMarginManager` but purpose-built for the
 * CA empty-state case: live PP-CA margin count is historically 0, so
 * this screen MUST work from zero rules. Missing rows are surfaced as
 * "Not configured" and the bulk apply CREATES new `country_default`
 * rules by default (`create_if_missing=true`) — the USA screen keeps
 * its stricter "skip missing" behaviour on the legacy endpoint.
 *
 * Country: CA · Currency: CAD · UOM: L · Margin unit: ¢/L.
 * Portal visibility (`price_report_networks`) is NOT edited here.
 * Country-lane isolation is enforced end-to-end — a CA edit here can
 * never mutate a PP-US rule.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import BackdatedConfirmModal from "../../components/BackdatedConfirmModal";

function CentsPerL(v) {
  if (typeof v !== "number" || Number.isNaN(v)) return "Not configured";
  return `${v.toFixed(2)} ¢/L`;
}

export default function PetroPassCanadaMarginManager() {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | has_default | no_default
  const [editing, setEditing] = useState(null);
  const [newMargin, setNewMargin] = useState("");
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkMargin, setBulkMargin] = useState("");
  const [bulkConfirm, setBulkConfirm] = useState(false);
  // Phase 3E-9Q.2 — defensive backdated 409 handler. Today PP-CA never
  // sends `effective_date` (defaults to server today), but if a future
  // UI change adds an eff-date input this handler already picks up the
  // 409 BACKDATED_MARGIN_CONFIRMATION_REQUIRED response and shows the
  // shared confirmation modal.
  const [backdated, setBackdated] = useState(null);

  // Parse a fetch Response for the backend backdated-guard 409 shape.
  // Returns { code, effective_date, ... } when the body matches, else null.
  async function _readBackdatedError(resp) {
    if (resp.status !== 409) return null;
    try {
      const body = await resp.clone().json();
      const d = body?.detail;
      if (d?.code === "BACKDATED_MARGIN_CONFIRMATION_REQUIRED") return d;
    } catch { /* not JSON */ }
    return null;
  }

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(
        `${backend}/api/admin/petro-pass-margins?country=CA`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(data.rows || []);
      setMeta({
        customer_count: data.customer_count,
        default_rule_count: data.default_rule_count,
        missing_default_count: data.missing_default_count,
        distinct_default_values: data.distinct_default_values || [],
      });
    } catch (e) {
      toast.error(`Failed to load Petro-Pass Canada margins: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (q) {
      const needle = q.toLowerCase();
      out = out.filter((r) =>
        (r.company_name || "").toLowerCase().includes(needle) ||
        (r.customer_id || "").toLowerCase().includes(needle),
      );
    }
    if (filter === "has_default") {
      out = out.filter((r) => r.default_margin_cpl !== null);
    } else if (filter === "no_default") {
      out = out.filter((r) => r.default_margin_cpl === null);
    }
    return out;
  }, [rows, q, filter]);

  function toggleSelect(cid) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  }
  function selectAllFiltered() {
    const next = new Set(selected);
    filtered.forEach((r) => next.add(r.customer_id));
    setSelected(next);
  }
  function clearSelection() { setSelected(new Set()); }

  async function saveIndividual() {
    if (!editing) return;
    const val = parseFloat(newMargin);
    if (!Number.isFinite(val) || val < 0) {
      toast.error("Enter a valid ¢/L value.");
      return;
    }
    setSaving(true);
    try {
      // Editing an existing rule uses the shared PUT /margins/{id};
      // creating the first rule for a customer uses the country-aware
      // bulk endpoint with a single customer_id (create_if_missing=true).
      let resp;
      if (editing.default_margin_id) {
        resp = await fetch(
          `${backend}/api/margins/${editing.default_margin_id}`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ margin_cpl: val }),
          },
        );
      } else {
        resp = await fetch(
          `${backend}/api/admin/petro-pass-margins/bulk-update`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              country: "CA",
              customer_ids: [editing.customer_id],
              new_margin_cpl: val,
              create_if_missing: true,
            }),
          },
        );
      }
      if (!resp.ok) {
        const bd = await _readBackdatedError(resp);
        if (bd) {
          const eff = bd.effective_date;
          const val0 = val;
          const doIt = async () => {
            const r2 = await fetch(
              editing.default_margin_id
                ? `${backend}/api/margins/${editing.default_margin_id}?confirm_backdated=true`
                : `${backend}/api/admin/petro-pass-margins/bulk-update?confirm_backdated=true`,
              {
                method: editing.default_margin_id ? "PUT" : "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(editing.default_margin_id
                  ? { margin_cpl: val0 }
                  : { country: "CA", customer_ids: [editing.customer_id], new_margin_cpl: val0, create_if_missing: true }),
              },
            );
            if (!r2.ok) throw new Error(`HTTP ${r2.status}`);
          };
          setBackdated({
            open: true, effectiveDate: eff,
            contextLabel: `Set PP-CA margin for ${editing.company_name}`,
            customerCount: 1, ruleCount: 1,
            retry: async () => {
              setBackdated((b) => b && { ...b, busy: true });
              try { await doIt(); setBackdated(null); toast.success(`${editing.company_name}: set to ${val.toFixed(2)} ¢/L`); setEditing(null); setNewMargin(""); await load(); }
              catch (e) { toast.error(`Update failed: ${e.message}`); setBackdated((b) => b && { ...b, busy: false }); }
            },
          });
          setSaving(false);
          return;
        }
        throw new Error(`HTTP ${resp.status}`);
      }
      toast.success(`${editing.company_name}: set to ${val.toFixed(2)} ¢/L`);
      setEditing(null); setNewMargin("");
      await load();
    } catch (e) {
      toast.error(`Update failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function applyBulk() {
    const val = parseFloat(bulkMargin);
    if (!Number.isFinite(val) || val < 0) {
      toast.error("Enter a valid ¢/L value.");
      return;
    }
    if (selected.size === 0) {
      toast.error("Select at least one customer.");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(
        `${backend}/api/admin/petro-pass-margins/bulk-update`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            country: "CA",
            customer_ids: Array.from(selected),
            new_margin_cpl: val,
            create_if_missing: true,
          }),
        },
      );
      if (!r.ok) {
        const bd = await _readBackdatedError(r);
        if (bd) {
          const val0 = val;
          const cids = Array.from(selected);
          setBackdated({
            open: true, effectiveDate: bd.effective_date,
            contextLabel: `Bulk-set PP-CA margin for ${cids.length} customer${cids.length === 1 ? "" : "s"}`,
            customerCount: cids.length, ruleCount: cids.length,
            retry: async () => {
              setBackdated((b) => b && { ...b, busy: true });
              try {
                const r2 = await fetch(
                  `${backend}/api/admin/petro-pass-margins/bulk-update?confirm_backdated=true`,
                  { method: "POST", credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ country: "CA", customer_ids: cids,
                                           new_margin_cpl: val0, create_if_missing: true }) },
                );
                if (!r2.ok) throw new Error(`HTTP ${r2.status}`);
                const data2 = await r2.json();
                toast.success(`Bulk: ${data2.created_count} created · ${data2.updated_count} updated · ${data2.unchanged_count} unchanged`);
                setBulkMargin(""); setBulkConfirm(false); clearSelection();
                setBackdated(null);
                await load();
              } catch (e) { toast.error(`Bulk failed: ${e.message}`); setBackdated((b) => b && { ...b, busy: false }); }
            },
          });
          setSaving(false);
          return;
        }
        throw new Error(`HTTP ${r.status}`);
      }
      const data = await r.json();
      toast.success(
        `Bulk: ${data.created_count} created · ${data.updated_count} updated · ${data.unchanged_count} unchanged`,
      );
      setBulkMargin(""); setBulkConfirm(false); clearSelection();
      await load();
    } catch (e) {
      toast.error(`Bulk update failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6 space-y-6" data-testid="pp-ca-margin-manager">
      <div>
        <h1 className="text-3xl font-semibold">Petro-Pass Canada Margins</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Country <b>Canada</b> · Network <b>Petro-Pass</b> · Currency <b>CAD/L</b> · Unit <b>¢/L</b>.
          Each row is an editable customer-specific margin rule. Portal
          visibility is <b>separate</b> — it is not toggled from this screen.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="gf-card p-4"><div className="overline text-[10px]">Active customers</div><div className="text-2xl font-semibold" data-testid="pp-ca-summary-customer-count">{meta.customer_count ?? "—"}</div></div>
        <div className="gf-card p-4"><div className="overline text-[10px]">With Petro-Pass Canada default</div><div className="text-2xl font-semibold" data-testid="pp-ca-summary-with-default">{meta.default_rule_count ?? "—"}</div></div>
        <div className="gf-card p-4"><div className="overline text-[10px]">Not configured</div><div className="text-2xl font-semibold" data-testid="pp-ca-summary-missing">{meta.missing_default_count ?? "—"}</div></div>
        <div className="gf-card p-4"><div className="overline text-[10px]">Distinct margin values</div><div className="text-sm font-mono">{(meta.distinct_default_values || []).map((v) => `${Number(v).toFixed(2)}`).join(", ") || "—"}</div></div>
      </div>

      {meta.default_rule_count === 0 && (
        <div className="gf-card p-4 border-l-4 border-amber-400" data-testid="pp-ca-empty-state">
          <div className="font-semibold">No Petro-Pass Canada margins configured yet.</div>
          <div className="text-sm text-zinc-500 mt-1">
            Select one or more customers below and set a default margin in ¢/L
            (CAD). Backend uniqueness includes country, so this cannot collide
            with any Petro-Pass USA rule you have already configured.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input
          className="gf-input md:col-span-2"
          placeholder="Search customer, id…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="pp-ca-search"
        />
        <select
          className="gf-input"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="pp-ca-filter"
        >
          <option value="all">All active customers</option>
          <option value="has_default">Has Petro-Pass Canada default</option>
          <option value="no_default">Missing default (not configured)</option>
        </select>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button className="gf-btn gf-btn-outline" onClick={selectAllFiltered} data-testid="pp-ca-select-all">Select all filtered ({filtered.length})</button>
        <button className="gf-btn gf-btn-outline" onClick={clearSelection} data-testid="pp-ca-clear-selection">Clear selection ({selected.size})</button>
        <div className="flex items-center gap-2 ml-auto">
          <input
            className="gf-input"
            style={{ width: 120 }}
            placeholder="e.g. 6.0"
            value={bulkMargin}
            onChange={(e) => setBulkMargin(e.target.value)}
            data-testid="pp-ca-bulk-margin-input"
          />
          <span className="text-xs text-zinc-500">¢/L</span>
          {!bulkConfirm ? (
            <button
              className="gf-btn"
              disabled={saving || selected.size === 0 || !bulkMargin}
              onClick={() => setBulkConfirm(true)}
              data-testid="pp-ca-bulk-apply"
            >
              Apply to {selected.size} selected
            </button>
          ) : (
            <>
              <button
                className="gf-btn gf-btn-outline"
                disabled={saving}
                onClick={() => setBulkConfirm(false)}
                data-testid="pp-ca-bulk-cancel"
              >
                Cancel
              </button>
              <button
                className="gf-btn gf-btn-primary"
                disabled={saving}
                onClick={applyBulk}
                data-testid="pp-ca-bulk-confirm"
              >
                Confirm — set {selected.size} customers to {bulkMargin} ¢/L
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500">Loading Petro-Pass Canada margins…</div>
      ) : (
        <table className="gf-table" data-testid="pp-ca-margin-table">
          <thead>
            <tr>
              <th />
              <th>CUSTOMER</th>
              <th>CUSTOMER ID</th>
              <th className="text-right">DEFAULT MARGIN (¢/L)</th>
              <th>EFFECTIVE DATE</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.customer_id} data-testid={`pp-ca-row-${r.customer_id}`}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(r.customer_id)}
                    onChange={() => toggleSelect(r.customer_id)}
                    data-testid={`pp-ca-select-${r.customer_id}`}
                  />
                </td>
                <td>{r.company_name}</td>
                <td className="font-mono text-xs">{r.customer_id}</td>
                <td className="gf-num">{CentsPerL(r.default_margin_cpl)}</td>
                <td className="font-mono text-xs">{r.default_effective_date || "—"}</td>
                <td>
                  <button
                    className="gf-btn gf-btn-outline gf-btn-sm"
                    onClick={() => {
                      setEditing(r);
                      setNewMargin(
                        typeof r.default_margin_cpl === "number"
                          ? r.default_margin_cpl.toFixed(2)
                          : "",
                      );
                    }}
                    data-testid={`pp-ca-edit-${r.customer_id}`}
                  >
                    {r.default_margin_id ? "Edit" : "Set margin"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="pp-ca-edit-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md space-y-3">
            <h2 className="text-xl font-semibold">
              {editing.default_margin_id ? "Edit" : "Set"} Petro-Pass Canada margin
            </h2>
            <div className="text-sm text-zinc-500">
              {editing.company_name} · <span className="font-mono text-xs">{editing.customer_id}</span>
            </div>
            <label className="block text-sm">
              New margin (¢/L)
              <input
                type="number"
                step="0.01"
                min="0"
                className="gf-input w-full mt-1"
                value={newMargin}
                onChange={(e) => setNewMargin(e.target.value)}
                data-testid="pp-ca-edit-margin-input"
                autoFocus
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button className="gf-btn gf-btn-outline" disabled={saving} onClick={() => { setEditing(null); setNewMargin(""); }} data-testid="pp-ca-edit-cancel">
                Cancel
              </button>
              <button className="gf-btn gf-btn-primary" disabled={saving} onClick={saveIndividual} data-testid="pp-ca-edit-save">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 3E-9Q.2 — shared backdated pricing-change guard. */}
      <BackdatedConfirmModal
        open={!!backdated?.open}
        onCancel={() => setBackdated(null)}
        onConfirm={() => backdated?.retry && backdated.retry()}
        effectiveDate={backdated?.effectiveDate}
        contextLabel={backdated?.contextLabel}
        customerCount={backdated?.customerCount}
        ruleCount={backdated?.ruleCount}
        busy={!!backdated?.busy}
      />
    </div>
  );
}
